import { Outlet } from '@tanstack/react-router';
import { type CSSProperties, useState } from 'react';
import {
  IMPERSONATION_BANNER_HEIGHT,
  ImpersonationBanner,
} from '@/components/custom/impersonation-banner';
import {
  NOTIFICATION_BANNER_HEIGHT,
  NotificationBanner,
} from '@/components/custom/notification-banner';
import { SessionIdleWarning } from '@/components/custom/session-idle-warning';
import { TimerHud } from '@/components/custom/timer-hud';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { useLogRocketIdentify } from '@/lib/logrocket';
import { useMe } from '@/lib/me';
import { useTimezoneSync } from '@/lib/use-timezone-sync';
import { AppHeader } from './app-header';
import { AppSidebar } from './app-sidebar';
import { PageTitleSlotProvider } from './page-title-slot';
import {
  SecondarySidebarSlotProvider,
  useSecondarySidebarSlotValue,
} from './secondary-sidebar-slot';

export function AppLayout() {
  // Sync browser-detected IANA tz to users.timezone on first authenticated
  // load (no-op unless the stored value is still the default 'UTC').
  useTimezoneSync();
  useLogRocketIdentify();
  // Collapsed (icon rail) by default. Expanding it holds for the rest of the
  // browser session (reloads included); a new tab or window starts collapsed.
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarExpanded);
  const onSidebarOpenChange = (open: boolean) => {
    setSidebarOpen(open);
    writeSidebarExpanded(open);
  };
  // Both banners are `position: fixed` so toggling either doesn't reflow the
  // column — we reserve their combined height as top padding on the shell
  // instead. The announcement banner reports only WHETHER it's showing
  // (its height is always the fixed `NOTIFICATION_BANNER_HEIGHT` constant,
  // never actually variable) — the impersonation banner then renders BELOW
  // it via `offsetTop`, and the announcement banner always takes the top
  // slot, since site-wide broadcast content outranks the impersonation
  // notice.
  const { impersonation } = useMe();
  const [announcementVisible, setAnnouncementVisible] = useState(false);
  const announcementBannerHeight = announcementVisible ? NOTIFICATION_BANNER_HEIGHT : 0;
  const padTop =
    announcementBannerHeight + (impersonation.impersonating ? IMPERSONATION_BANNER_HEIGHT : 0);
  // And we PUBLISH what is left, because `100svh` is the window and this
  // column is the window less that padding. Anything sizing itself against the
  // viewport from inside — the report builder's pinned config column and its
  // listing, today — reads this instead and stops hanging a banner's worth of
  // content below the fold while someone is impersonating.
  return (
    <SidebarProvider
      open={sidebarOpen}
      onOpenChange={onSidebarOpenChange}
      style={{ '--sidebar-width': '248px', '--sidebar-width-icon': '64px' } as React.CSSProperties}
    >
      <PageTitleSlotProvider>
        <SecondarySidebarSlotProvider>
          <AppSidebar offsetTop={padTop} />
          <AppShellBody padTop={padTop} />
          <AppHeader offsetTop={padTop} />
        </SecondarySidebarSlotProvider>
      </PageTitleSlotProvider>
      <NotificationBanner onVisibilityChange={setAnnouncementVisible} />
      <ImpersonationBanner offsetTop={announcementBannerHeight} />
      {/* Top-center heads-up display: the single entry point for
          timers across the app — start a new one, see paused, or
          control the running one. */}
      <TimerHud offsetTop={padTop} />
      {/* Idle-based sign-out: after 30 min of no input, a 2-minute
          prompt fires; if the user doesn't respond, signOut + bounce
          to /login. Suppressed while a timer is running (which also keeps
          the server session alive). Mounted here so it's live on every
          authenticated page and torn down on sign-out. */}
      <SessionIdleWarning />
    </SidebarProvider>
  );
}

/**
 * Session-scoped on purpose. The shadcn `SidebarProvider` also writes a 7-day
 * `sidebar_state` cookie on every toggle, but nothing reads it, so it has no
 * effect. Storage can throw (private mode, blocked site data) — fall back to
 * collapsed.
 */
const SIDEBAR_EXPANDED_KEY = 'clockwise.sidebarExpanded';

function readSidebarExpanded(): boolean {
  try {
    return sessionStorage.getItem(SIDEBAR_EXPANDED_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeSidebarExpanded(open: boolean) {
  try {
    sessionStorage.setItem(SIDEBAR_EXPANDED_KEY, String(open));
  } catch {
    // Not remembered; the toggle itself still works.
  }
}

/**
 * The main content column: an optional full-height secondary sidebar (the
 * `/setup` sub-nav, published via `useSecondarySidebarSlot`) beside the
 * routed page. Split out from `AppLayout` only so it can read the slot —
 * `useSecondarySidebarSlotValue` needs to run inside `SecondarySidebarSlotProvider`.
 *
 * The secondary column is a sibling of the `mt-16` content div, not nested
 * inside it, so it spans the full height of `SidebarInset` (y=0 to the
 * bottom) instead of being pushed down to clear the fixed `AppHeader` — see
 * `secondary-sidebar-slot.tsx` for why nesting it in the outlet doesn't work.
 */
function AppShellBody({ padTop }: { padTop: number }) {
  const secondarySidebar = useSecondarySidebarSlotValue();
  return (
    <SidebarInset
      className="flex h-svh min-w-0 flex-row overflow-hidden"
      style={
        {
          paddingTop: padTop,
          '--app-viewport': `calc(100svh - ${padTop}px)`,
        } as CSSProperties
      }
    >
      {secondarySidebar}
      <div className="min-h-0 min-w-0 flex-1 overflow-hidden mt-16">
        <Outlet />
      </div>
    </SidebarInset>
  );
}
